import React, { useCallback, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { soaApi } from '@/services/api';
import { SoaDisclaimerStatus } from '@/types/soa';
import styles from './SoaList.module.css';

interface SoaDisclaimerBarProps {
    /** Notifies the parent so the Send button can be blocked until one exists. */
    onStatusChange?: (status: SoaDisclaimerStatus) => void;
    /** 'chip' sits inline in the page toolbar; 'row' fills the width of a modal field. */
    variant?: 'chip' | 'row';
}

/**
 * Whether a disclaimer PDF is stored, with view/replace. Monthly statements append
 * it, and viewing is the only check that the right document reaches clients.
 */
const SoaDisclaimerBar: React.FC<SoaDisclaimerBarProps> = ({ onStatusChange, variant = 'chip' }) => {
    const [status, setStatus] = useState<SoaDisclaimerStatus | null>(null);
    const [busy, setBusy] = useState(false);

    const refresh = useCallback(async (isCancelled?: () => boolean) => {
        try {
            const nextStatus = await soaApi.getDisclaimerStatus();
            if (isCancelled?.()) return;
            setStatus(nextStatus);
            onStatusChange?.(nextStatus);
        } catch {
            if (isCancelled?.()) return;
            const fallback = { exists: false, text: '', has_custom_text: false };
            setStatus(fallback);
            onStatusChange?.(fallback);
        }
    }, [onStatusChange]);

    useEffect(() => {
        let cancelled = false;
        refresh(() => cancelled);
        return () => { cancelled = true; };
    }, [refresh]);

    const handleUpload = async (file: File) => {
        setBusy(true);
        try {
            await soaApi.uploadDisclaimer(file);
            await refresh();
            Swal.fire('Disclaimer saved', 'It will be appended to monthly statements.', 'success');
        } catch {
            Swal.fire('Upload failed', 'Could not save the disclaimer.', 'error');
        } finally {
            setBusy(false);
        }
    };

    const handleView = async () => {
        const tab = window.open('', '_blank');
        try {
            const blob = await soaApi.downloadDisclaimer();
            const url = window.URL.createObjectURL(blob);
            if (tab) {
                tab.location.href = url;
            } else {
                window.open(url, '_blank');
            }
        } catch {
            tab?.close();
            Swal.fire('Could not open', 'Failed to load the disclaimer.', 'error');
        }
    };

    if (status === null) return null;
    const { exists } = status;

    const shape = variant === 'row' ? styles['disclaimer-row'] : styles.disclaimer;

    return (
        <span
            className={`${shape} ${exists ? styles['disclaimer-ok'] : styles['disclaimer-missing']}`}
            title={exists
                ? 'Appended to every monthly statement.'
                : 'Monthly statements cannot be sent until one is added.'}
        >
            <span className={variant === 'row' ? styles['disclaimer-row-label'] : undefined}>
                {exists ? 'Disclaimer stored' : 'No disclaimer'}
            </span>

            <span className={styles['disclaimer-actions']}>
                {exists && (
                    <button type="button" className={styles['disclaimer-action']} onClick={handleView}>
                        View
                    </button>
                )}
                <label
                    className={styles['disclaimer-action']}
                    style={{ cursor: busy ? 'wait' : 'pointer' }}
                >
                    {busy ? 'Uploading…' : exists ? 'Replace' : 'Upload'}
                    <input
                        type="file"
                        accept=".pdf"
                        hidden
                        disabled={busy}
                        onChange={event => {
                            const file = event.target.files?.[0];
                            if (file) handleUpload(file);
                            event.target.value = '';
                        }}
                    />
                </label>
            </span>
        </span>
    );
};

export default SoaDisclaimerBar;
